/// <reference types="@types/google.maps" />
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { Card } from 'components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { getTrackingMapData, deletePing, restorePing, type TrackingMapData } from '../api/shipments';
import { formatDateTime } from 'lib/utils';
import * as HeroIcons from '@heroicons/react/24/outline';
import { ManualPingModal } from './ManualPingModal';
import { toast } from 'sonner';
import { ConfirmDeletePingDialog } from './ConfirmDeletePingModal';
import { LhcModal } from './LhcModal';
import { Button } from 'components/ui/button';

const mapContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%'
};

interface GoogleMapComponentProps {
  mapData: TrackingMapData;
  selectedSource: 'all' | 'sim' | 'fastag' | 'gps' | 'manual';
  apiKey: string;
  onDeletePing: (pingId: number, pingType: string, pingIndex: number) => void;
}

interface MarkerWithTimestamp {
  location: google.maps.LatLngLiteral;
  stopover: boolean;
  timestamp: Date;
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({ 
  mapData, 
  selectedSource, 
  apiKey,
  onDeletePing
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);

  // Initialize map
  const initializeMap = useCallback((ref: HTMLDivElement | null) => {
    if (ref && window.google && !map) {
      const googleMap = new window.google.maps.Map(ref, {
        zoom: 6,
        center: { lat: 20.5937, lng: 78.9629 }, // Center of India
        mapTypeId: 'roadmap',
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: true,
      });

      const dirService = new window.google.maps.DirectionsService();
      const dirRenderer = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#8B5CF6',
          strokeOpacity: 0.7,
          strokeWeight: 4
        }
      });

      dirRenderer.setMap(googleMap);

      const infoWin = new window.google.maps.InfoWindow();
      
      setMap(googleMap);
      setDirectionsService(dirService);
      setDirectionsRenderer(dirRenderer);
      setInfoWindow(infoWin);
    }
  }, [map]);

  // Create marker icons
  const createMarkerIcon = useCallback((type: 'origin' | 'destination' | 'sim' | 'fastag' | 'gps' | 'manual' | 'incident', number?: number) => {
    const icons: Record<string, { url: string; scaledSize: google.maps.Size; anchor: google.maps.Point }> = {
      origin: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="20" cy="20" r="16" fill="#10B981" stroke="#ffffff" stroke-width="3" filter="url(#shadow)"/>
            <circle cx="20" cy="20" r="10" fill="#ffffff"/>
            <path d="M20 12 L20 28 M12 20 L28 20" stroke="#10B981" stroke-width="3" stroke-linecap="round"/>
            <circle cx="20" cy="20" r="3" fill="#10B981"/>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20)
      },
      destination: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="20" cy="20" r="16" fill="#EF4444" stroke="#ffffff" stroke-width="3" filter="url(#shadow)"/>
            <circle cx="20" cy="20" r="10" fill="#ffffff"/>
            <rect x="15" y="15" width="10" height="10" fill="#EF4444" rx="2"/>
            <rect x="17" y="17" width="6" height="6" fill="#ffffff" rx="1"/>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20)
      },
      sim: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="16" cy="16" r="14" fill="#3B82F6" stroke="#ffffff" stroke-width="2" filter="url(#shadow)"/>
            <text x="16" y="20" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="bold" font-family="Arial">${number || ''}</text>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      },
      fastag: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="16" cy="16" r="14" fill="#EF4444" stroke="#ffffff" stroke-width="2" filter="url(#shadow)"/>
            <text x="16" y="20" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="bold" font-family="Arial">${number || ''}</text>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      },
      gps: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="16" cy="16" r="14" fill="#F97316" stroke="#ffffff" stroke-width="2" filter="url(#shadow)"/>
            <text x="16" y="20" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="bold">${number || ''}</text>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      },
      manual: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" flood-opacity="0.3"/>
              </filter>
            </defs>
            <circle cx="16" cy="16" r="14" fill="#10B981" stroke="#ffffff" stroke-width="2" filter="url(#shadow)"/>
            <text x="16" y="20" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="bold" font-family="Arial">${number || ''}</text>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      },
      incident: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="16" fill="#DC2626" stroke="#ffffff" stroke-width="3"/>
            <text x="18" y="23" text-anchor="middle" fill="#ffffff" font-size="18" font-weight="bold">!</text>
          </svg>
        `)}`,
        scaledSize: new google.maps.Size(36, 36),
        anchor: new google.maps.Point(18, 18)
      }
    };
    return icons[type];
  }, []);

  // Set up global delete function for info window buttons
  useEffect(() => {
    (window as any).deletePing = onDeletePing;
    return () => {
      delete (window as any).deletePing;
    };
  }, [onDeletePing]);

  // Update markers based on selected source and add tooltips
  useEffect(() => {
    if (!map || !mapData || !infoWindow) return;

    // Clear existing markers
    const currentMarkers = markers;
    currentMarkers.forEach(marker => marker.setMap(null));
    const newMarkers: google.maps.Marker[] = [];

    const addClickListener = (marker: google.maps.Marker, content: string) => {
      marker.addListener('click', () => {
        infoWindow.setContent(content);
        infoWindow.open(map, marker);
      });
    };

    // Add origin marker
    if (mapData.origin?.lat && mapData.origin?.lon) {
      const originMarker = new window.google.maps.Marker({
        position: { lat: mapData.origin.lat, lng: mapData.origin.lon },
        map: map,
        title: `Origin: ${mapData.origin.address}`,
        icon: createMarkerIcon('origin')
      });
      
      const infoContent = `
        <div style="padding: 8px; max-width: 200px;">
          <div style="font-weight: bold; color: #10B981; margin-bottom: 4px;">Origin</div>
          <div style="font-size: 14px;">${mapData.origin.address}</div>
        </div>
      `;
      addClickListener(originMarker, infoContent);
      newMarkers.push(originMarker);
    }

    // Add destination marker
    if (mapData.destination?.lat && mapData.destination?.lon) {
      const destMarker = new window.google.maps.Marker({
        position: { lat: mapData.destination.lat, lng: mapData.destination.lon },
        map: map,
        title: `Destination: ${mapData.destination.address}`,
        icon: createMarkerIcon('destination')
      });
      
      const infoContent = `
        <div style="padding: 8px; max-width: 200px;">
          <div style="font-weight: bold; color: #EF4444; margin-bottom: 4px;">Destination</div>
          <div style="font-size: 14px;">${mapData.destination.address}</div>
        </div>
      `;
      addClickListener(destMarker, infoContent);
      newMarkers.push(destMarker);
    }

    // Add SIM markers
    if ((selectedSource === 'all' || selectedSource === 'sim') && mapData.markers?.sim) {
      // Sort by timestamp and add numbered markers
      const sortedSimMarkers = [...mapData.markers.sim].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      sortedSimMarkers.forEach((markerData, index) => {
        const simMarker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lon },
          map: map,
          title: `SIM ${index + 1}: ${markerData.address}`,
          icon: createMarkerIcon('sim', index + 1)
        });
        
        const timestamp = formatDateTime(markerData.timestamp);
        const infoContent = `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: bold; color: #3B82F6; margin-bottom: 4px;">SIM Location ${index + 1}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">${markerData.address}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${timestamp.date} ${timestamp.time}</div>
            <button 
              onclick="window.requestDeletePing(${markerData.id}, 'SIM', ${index + 1})"
              style="background: #ef4444; color: white; border: none; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0 1 1 2 2 2v2"/>
              </svg>
              Delete
            </button>
          </div>
        `;
        addClickListener(simMarker, infoContent);
        newMarkers.push(simMarker);
      });
    }

    // Add Fastag markers
    if ((selectedSource === 'all' || selectedSource === 'fastag') && mapData.markers?.fastag) {
      // Sort by timestamp and add numbered markers
      const sortedFastagMarkers = [...mapData.markers.fastag].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      sortedFastagMarkers.forEach((markerData, index) => {
        const fastagMarker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lon },
          map: map,
          title: `Fastag ${index + 1}: ${markerData.address}`,
          icon: createMarkerIcon('fastag', index + 1)
        });
        
        const timestamp = formatDateTime(markerData.timestamp);
        const infoContent = `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: bold; color: #EF4444; margin-bottom: 4px;">Fastag Location ${index + 1}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">${markerData.address}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${timestamp.date} ${timestamp.time}</div>
            <button 
              onclick="window.requestDeletePing(${markerData.id}, 'Fastag', ${index + 1})"
              style="background: #ef4444; color: white; border: none; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0 1 1 2 2 2v2"/>
              </svg>
              Delete
            </button>
          </div>
        `;
        addClickListener(fastagMarker, infoContent);
        newMarkers.push(fastagMarker);
      });
    }

    // Add GPS markers
    if ((selectedSource === 'all' || selectedSource === 'gps') && mapData.markers?.gps) {

      const sortedGpsMarkers = [...mapData.markers.gps].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      sortedGpsMarkers.forEach((markerData, index) => {
        const gpsMarker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lon },
          map: map,
          title: `GPS ${index + 1}: ${markerData.address}`,
          icon: createMarkerIcon('gps', index + 1)
        });

        const timestamp = formatDateTime(markerData.timestamp);

        const infoContent = `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: bold; color: #F97316; margin-bottom: 4px;">GPS Location ${index + 1}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">${markerData.address}</div>
            <div style="font-size: 12px; color: #666;">${timestamp.date} ${timestamp.time}</div>
          </div>
        `;

        addClickListener(gpsMarker, infoContent);
        newMarkers.push(gpsMarker);
      });
    }

    // Add Manual markers
    if ((selectedSource === 'all' || selectedSource === 'manual') && mapData.markers?.manual) {
      // Sort by timestamp and add numbered markers
      const sortedManualMarkers = [...mapData.markers.manual].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      sortedManualMarkers.forEach((markerData, index) => {
        const manualMarker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lon },
          map: map,
          title: `Manual ${index + 1}: ${markerData.address}`,
          icon: createMarkerIcon('manual', index + 1)
        });
        
        const timestamp = formatDateTime(markerData.timestamp);
        const infoContent = `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: bold; color: #10B981; margin-bottom: 4px;">Manual Location ${index + 1}</div>
            <div style="font-size: 14px; margin-bottom: 4px;">${markerData.address}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">${timestamp.date} ${timestamp.time}</div>
            <button 
              onclick="window.requestDeletePing(${markerData.id}, 'Manual', ${index + 1})"
              style="background: #ef4444; color: white; border: none; padding: 4px 8px; border-radius: 4px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px;"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0 1 1 2 2 2v2"/>
              </svg>
              Delete
            </button>
          </div>
        `;
        addClickListener(manualMarker, infoContent);
        newMarkers.push(manualMarker);
      });
    }

    // Add Incident markers
    if (mapData.markers?.incident) {
      mapData.markers.incident.forEach((markerData) => {
        const incidentMarker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lon },
          map: map,
          title: `Incident: ${markerData.address}`,
          icon: createMarkerIcon('incident')
        });

        const timestamp = formatDateTime(markerData.timestamp);

        const infoContent = `
          <div style="padding: 8px; max-width: 220px;">
            <div style="font-weight: bold; color: #DC2626; margin-bottom: 4px;">
              ⚠ Incident Occurred
            </div>
            <div style="font-size: 14px; margin-bottom: 4px;">
              ${markerData.address}
            </div>
            <div style="font-size: 12px; color: #666;">
              ${timestamp.date} ${timestamp.time}
            </div>
          </div>
        `;

        addClickListener(incidentMarker, infoContent);
        newMarkers.push(incidentMarker);
      });
    }

    setMarkers(newMarkers);
  }, [map, mapData, selectedSource, infoWindow, onDeletePing]);

  // Calculate and display route
  useEffect(() => {
    if (!map || !directionsService || !directionsRenderer || !mapData) return;

    const calculateRoute = async () => {

      try {
        if (!mapData.origin?.lat || !mapData.origin?.lon || !mapData.destination?.lat || !mapData.destination?.lon) {
          console.error('Origin or destination coordinates missing');
          return;
        }

        const origin = { lat: mapData.origin.lat, lng: mapData.origin.lon };
        const destination = { lat: mapData.destination.lat, lng: mapData.destination.lon };

        // Collect all waypoints based on selected source
        const allWaypoints: MarkerWithTimestamp[] = [];
        
        if (selectedSource === 'all' || selectedSource === 'sim') {
          if (mapData.markers?.sim) {
            const sortedSim = [...mapData.markers.sim].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            sortedSim.forEach(marker => {
              allWaypoints.push({
                location: { lat: marker.lat, lng: marker.lon },
                stopover: true,
                timestamp: new Date(marker.timestamp)
              });
            });
          }
        }

        if (selectedSource === 'all' || selectedSource === 'fastag') {
          if (mapData.markers?.fastag) {
            const sortedFastag = [...mapData.markers.fastag].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            sortedFastag.forEach(marker => {
              allWaypoints.push({
                location: { lat: marker.lat, lng: marker.lon },
                stopover: true,
                timestamp: new Date(marker.timestamp)
              });
            });
          }
        }

        if (selectedSource === 'all' || selectedSource === 'manual') {
          if (mapData.markers?.manual) {
            const sortedManual = [...mapData.markers.manual].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            sortedManual.forEach(marker => {
              allWaypoints.push({
                location: { lat: marker.lat, lng: marker.lon },
                stopover: true,
                timestamp: new Date(marker.timestamp)
              });
            });
          }
        }

        // Sort all waypoints by timestamp to create chronological route
        const sortedWaypoints = allWaypoints
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
          .map(wp => ({ location: wp.location, stopover: wp.stopover }))
          .slice(0, 23); // Google Maps API limit (25 total including origin and destination)

        console.log('Route calculation:', {
          origin: origin,
          destination: destination,
          waypoints: sortedWaypoints,
          selectedSource
        });

        const request: google.maps.DirectionsRequest = {
          origin: origin,
          destination: destination,
          waypoints: sortedWaypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false
        };

        directionsService.route(request, (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatus) => {
          if (status === 'OK' && result) {
            console.log('Directions calculated successfully');
            directionsRenderer.setDirections(result);
            
            // Fit map to show the entire route
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(origin);
            bounds.extend(destination);
            sortedWaypoints.forEach(wp => {
              bounds.extend(wp.location);
            });
            map.fitBounds(bounds);
          } else {
            console.error('Directions request failed:', status);
            // Fallback: just show a basic route without waypoints
            const fallbackRequest: google.maps.DirectionsRequest = {
              origin: origin,
              destination: destination,
              travelMode: google.maps.TravelMode.DRIVING
            };
            
            directionsService.route(fallbackRequest, (fallbackResult: google.maps.DirectionsResult | null, fallbackStatus: google.maps.DirectionsStatus) => {
              if (fallbackStatus === 'OK' && fallbackResult) {
                console.log('Fallback directions calculated successfully');
                directionsRenderer.setDirections(fallbackResult);
                
                // Fit map bounds for fallback route
                const bounds = new google.maps.LatLngBounds();
                bounds.extend(origin);
                bounds.extend(destination);
                map.fitBounds(bounds);
              } else {
                console.error('Fallback directions also failed:', fallbackStatus);
              }
            });
          }
        });

      } catch (error) {
        console.error('Error calculating route:', error);
      }
    };

    // Add delay to ensure markers are placed before calculating route
    const timer = setTimeout(calculateRoute, 1000);
    
    return () => clearTimeout(timer);
  }, [map, directionsService, directionsRenderer, mapData, selectedSource]);

  return <div ref={initializeMap} style={mapContainerStyle} />;
};

interface TrackingMapProps {
  cnNumber: string;
  onDataRefresh?: () => void;
}

const TrackingMap: React.FC<TrackingMapProps> = ({ cnNumber, onDataRefresh }) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; type: string; index: number } | null>(null);
  const [mapData, setMapData] = useState<TrackingMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<'all' | 'sim' | 'fastag' | 'manual'>('all');
  const [showManualPingModal, setShowManualPingModal] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Fetch map data
  useEffect(() => {
    let isMounted = true;

    const fetchMapData = async () => {
      if (!cnNumber) return;

      try {
        setLoading(true);
        setError(null);
        
        const response = await getTrackingMapData(cnNumber);
        
        if (isMounted) {
          if (response.success) {
            setMapData(response);
          } else {
            setError('Map data not available');
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching map data:', err);
          setError('Failed to load map data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMapData();

    return () => {
      isMounted = false;
    };
  }, [cnNumber]);

  useEffect(() => {
    (window as any).requestDeletePing = (id: number, type: string, index: number) => {
      setPendingDelete({ id, type, index });
      setConfirmDeleteOpen(true);
    };
    return () => {
      delete (window as any).requestDeletePing;
    };
  }, []);

  // Get marker counts
  const markerCounts = useMemo(() => {
    if (!mapData?.markers) return { sim: 0, fastag: 0, gps: 0, manual: 0, total: 0 };
    
    const simCount = mapData.markers.sim?.length || 0;
    const fastagCount = mapData.markers.fastag?.length || 0;
    const gpsCount = mapData.markers.gps?.length || 0;
    const manualCount = mapData.markers.manual?.length || 0;
    
    return {
      sim: simCount,
      fastag: fastagCount,
      gps: gpsCount,
      manual: manualCount,
      total: simCount + fastagCount + gpsCount + manualCount
    };
  }, [mapData]);

  const refreshAllData = useCallback(async () => {
    if (!cnNumber) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await getTrackingMapData(cnNumber);
      
      if (response.success) {
        setMapData(response);
      } else {
        setError('Map data not available');
      }
      // Also refresh tracking data in parent component
      onDataRefresh?.();
    } catch (err) {
      console.error('Error fetching map data:', err);
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  }, [cnNumber, onDataRefresh]);

  const handleManualPingSuccess = useCallback(() => {
    refreshAllData();
  }, [refreshAllData]);

  
   

  const handleRestorePing = useCallback(async (pingId: number) => {
    try {
      const response = await restorePing(pingId, cnNumber);
      
      if (response.success) {
        toast.success('Ping restored successfully', {
          style: {
            background: '#f0fdf4',
            color: '#15803d',
            border: '1px solid #bbf7d0'
          }
        });
        
        // Refresh data
        refreshAllData();
      } else {
        toast.error('Failed to restore ping', {
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca'
          }
        });
      }
    } catch (error) {
      console.error('Error restoring ping:', error);
      toast.error('Failed to restore ping', {
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca'
        }
      });
    }
  }, [refreshAllData, cnNumber]);

  const handleDeletePing = useCallback(async (pingId: number, pingType: string, pingIndex: number) => {
    try {
      const response = await deletePing(pingId, cnNumber);
      if (response.success) {
        toast.success('Ping deleted successfully', {
          action: {
            label: 'Undo',
            onClick: () => handleRestorePing(pingId)
          },
          style: { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }
        });
        refreshAllData();
      } else {
        toast.error('Failed to delete ping', {
          style: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
        });
      }
    } catch (error) {
      console.error('Error deleting ping:', error);
      toast.error('Failed to delete ping', {
        style: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    } finally {
      setConfirmDeleteOpen(false);
      setPendingDelete(null);
    }
  }, [refreshAllData, handleRestorePing, cnNumber]);

  if (!apiKey) {
    return (
      <Card className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-red-600 mb-2">Configuration Error</div>
          <div className="text-sm text-gray-600">Google Maps API key not found</div>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <div className="text-lg font-medium text-gray-900 mb-2">Loading map...</div>
          <div className="text-sm text-gray-600">Please wait</div>
        </div>
      </Card>
    );
  }

  if (error || !mapData) {
    return (
        <Card className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <HeroIcons.MapIcon className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <div className="text-lg font-medium text-gray-600 mb-2">Map view unavailable</div>
          <div className="text-sm text-gray-500">Unable to load tracking map</div>
        </div>
      </Card>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with controls */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-gray-700">SIM: {markerCounts.sim}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-gray-700">Fastag: {markerCounts.fastag}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
              <span className="text-gray-700">GPS: {markerCounts.gps}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-gray-700">Manual: {markerCounts.manual}</span>
            </span>
          </div>
          <button
            onClick={() => setShowManualPingModal(true)}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Add Manual Ping
          </button>
        </div>

        <div className='flex gap-x-4 justify-center items-center'>
          <Select value={selectedSource} onValueChange={(value: 'all' | 'sim' | 'fastag' | 'manual') => setSelectedSource(value)}>
            <SelectTrigger className="w-32 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="sim">SIM</SelectItem>
              <SelectItem value="fastag">Fastag</SelectItem>
              <SelectItem value="gps">GPS</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Map container */}
      <div className="flex-1 relative">
        <Wrapper apiKey={apiKey} libraries={['geometry']}>
          <GoogleMapComponent 
            mapData={mapData}
            selectedSource={selectedSource}
            apiKey={apiKey}
            onDeletePing={handleDeletePing}
          />
        </Wrapper>
      </div>
      
      <ManualPingModal
        isOpen={showManualPingModal}
        onClose={() => setShowManualPingModal(false)}
        cnNumber={cnNumber}
        onSuccess={handleManualPingSuccess}
      />
      <ConfirmDeletePingDialog
  open={confirmDeleteOpen}
  onOpenChange={(open) => {
    setConfirmDeleteOpen(open);
    if (!open) setPendingDelete(null);
  }}
  pingId={pendingDelete?.id}
  pingType={pendingDelete?.type}
  pingIndex={pendingDelete?.index}
  onConfirm={(id, type, index) => handleDeletePing(id, type, index)}
/>
    </div>
    
  );
};

export default TrackingMap;